<template>
  <div class="collection-form">
    <HeadSection title="Settings" :breadcrumbs="breadcrumbs" permissionOfSection="unified-collection">
      <template #actions>
        <a-col :span="14" class="text-right d-flex justify-content-end">
          <div>
            <a-button size="large" class="mr-4" @click="() => redirectToCollection(collectionName)"> Cancel</a-button>
            <a-button type="primary" size="large" :disabled="isSaveDisabled" @click="save" class="mr-4"> Save</a-button>
          </div>
        </a-col>
      </template>
    </HeadSection>
    <MainContainer :loading="userLoading" permissionOfSection="unified-collection">
      <a-form-model :rules="rules" :model="form" ref="form">
        <div class="d-flex flex-column">
          <a-row type="flex">
            <a-col flex="300px" class="mr-5">
              <a-form-model-item label="Collection name" prop="name" :colon="false">
                <a-input v-model="form.name" placeholder="Collection name" size="large" :disabled="loading" />
              </a-form-model-item>
            </a-col>
            <a-col flex="auto">
              <a-form-model-item ref="searchingValues" label="Collection item value" prop="searchingValues">
                <a-select v-model="form.searchingValues" mode="tags" size="large" :disabled="loading" @focus="onChangeValues" @blur="onEndValueEnter">
                  <template #placeholder><span style="color: #a1a1c2;">Enter query attributes (login, mail, etc.)</span></template>
                  <a-select-option v-for="option in options" :key="option.value">
                    {{ option.name}}
                  </a-select-option>
                </a-select>
              </a-form-model-item>
              <!-- <a-form-model-item label="Description" prop="description" :colon="false">
                <a-input v-model="form.description" placeholder="Description" size="large" :disabled="loading" />
              </a-form-model-item> -->
            </a-col>
          </a-row>
          <a-row type="flex">
            <a-col flex="auto">
              <a-form-model-item label="Description" prop="description" :colon="false">
                <a-textarea v-model="form.description" placeholder="Description" size="large" :disabled="loading" />
              </a-form-model-item>
            </a-col>
          </a-row>
          <a-row type="flex">
            <a-col flex="auto">
              <div class="d-flex flex-column">
                <div class="d-flex justify-content-between">
                  <span class="" style="font-weight: bold; position: relative; top: 4px;">Success repsponse</span>
                  <a-button type="link" @click="triggerFileInput">Import JSON</a-button>
                  <input type="file" ref="jsonInput" accept=".json,application/json" style="display: none"
                    @change="handleJsonUpload" />
                </div>
                <a-form-model-item prop="successResponse" :colon="false">
                  <a-textarea v-model="form.successResponse"
                    placeholder="'methods': { 'inwizo_p2psg' : { 'order':25 }, 'sbp_pnone': { 'order' : 29 }, 'bromoney': { 'order': 99}}"
                    :autoSize="{ minRows: 4}" :disabled="loading" />
                </a-form-model-item>
              </div>
            </a-col>
          </a-row>
          <div class="d-flex justify-content-between">
            <div class="">
              <a-icon class="text-default mr-3" type="info-circle" />
              <span>If value from this collection matches, this JSON will be returned in the API response</span>
            </div>
            <div>
              <span class="mr-2">Encrypted response</span>
              <a-switch v-model="form.isResponseEncrypted" />
            </div>
          </div>
        </div>
      </a-form-model>
    </MainContainer>
  </div>
</template>

<script>
import { mapState } from 'vuex'

import CollectionsService from '@/services/api/apiCollectionsService'
import HeadSection from '@/components/main/headSection'
import MainContainer from '@/components/main/mainContainer'

export default {
  name: 'ImportCollectionItemsModal',
  components: {
    HeadSection,
    MainContainer,
  },
  props: {
  },
  data() {
    return {
      form: {
        name: '',
        description: '',
        searchingValues: [],
        successResponse: '',
        isResponseEncrypted: false,
      },
      isSaveDisabled: false,
      loading: false,
      importFile: null,
      collection: null,
      options: [
      ],
      rules: {
        name: [
          {
            required: true,
            message: 'Collection name is required',
            trigger: 'blur',
          },
        ],
        searchingValues: [
          {
            type: 'array',
            required: true,
            message: 'Please input collection search attributes',
            trigger: 'change',
          },
        ],
      },
    }
  },
  computed: {
    ...mapState(['user']),
    userLoading() {
      return this.$store.state?.user?.loading || false
    },
    collectionId() {
      return this.$route.params.id
    },
    collectionName() {
      return decodeURIComponent(this.$route.params.name)
    },
    breadcrumbs() {
      return [
        { name: 'Collections', link: '/profiles/collections' },
        { name: this.collectionName, link: `/profiles/collections/items/${this.collectionId}/${this.collectionName}` },
        { name: 'Settings', link: undefined },
      ]
    },
  },
  async created() {
    this.collection = await this.getCollectionSettings()
    this.fillForm()
  },
  methods: {
    async save() {
      const isValid = await this.validateForm()
      if (!isValid) {
        return false
      }
      this.updateCollectionSettings()
    },
    async validateForm() {
      const isValid = await new Promise(resolve => {
        this.$refs.form.validate(resolve)
      })

      if (!isValid) return false

      if (this.form.successResponse) {
        try {
          JSON.parse(this.form.successResponse)
        } catch (e) {
          this.$notification.error({
            message: 'Invalid JSON',
            description: 'Please enter valid JSON in Success Response',
          })
          return false
        }
      }

      return true
    },
    onChangeValues() {
      this.isSaveDisabled = true
    },
    onEndValueEnter() {
      this.isSaveDisabled = false
    },
    triggerFileInput() {
      this.$refs.jsonInput.click()
    },
    handleJsonUpload(event) {
      const file = event.target.files[0]
      if (!file) return

      const reader = new FileReader()

      reader.onload = e => {
        try {
          const parsed = JSON.parse(e.target.result)
          this.form.successResponse = JSON.stringify(parsed, null, 2)
        } catch (err) {
          this.$notification.error({
            message: 'Invalid JSON file',
          })
        }
      }

      reader.readAsText(file)
      event.target.value = ''
    },
    validateJsonResponse() {
      try {
        JSON.parse(this.form.successResponse)
        return true
      } catch (err) {
        return false
      }
    },
    fillForm() {
      const { name, description, settings } = this.collection
      this.form.name = name
      this.form.description = description
      this.form.isResponseEncrypted = settings.encrypted_response
      this.form.searchingValues = settings.searching_values
      if (settings.service_response) {
        this.form.successResponse = JSON.stringify(settings.service_response, null, 2)
      }
    },
    redirectToCollection(newName = null) {
      const collectionName = newName || this.collectionName
      console.log(newName, this.collectionName)
      this.$router.push(`/profiles/collections/items/${this.collectionId}/${encodeURIComponent(collectionName)}`)
    },
    async getCollectionSettings() {
      try {
        this.loading = true
        const collectionsResponse = await CollectionsService.getCollection(this.collectionId)
        return collectionsResponse.data.data
      } catch (err) {
        let errorMessage = 'Something went wrong. Please try again later.'

        if (err.response?.data?.error?.message) {
          errorMessage = err.response.data.error.message
        } else if (err.message) {
          errorMessage = err.message
        }

        this.$notification.error({
          message: errorMessage,
        })
        this.loading = false
      } finally {
        this.loading = false
      }
    },
    async updateCollectionSettings() {
      this.loading = true
      try {
        const payload = {
          name: this.form.name,
          encrypted_response: this.form.isResponseEncrypted,
          searching_values: this.form.searchingValues,
          ...(this.form.description && { description: this.form.description }),
          ...(this.form.successResponse && { service_response: JSON.parse(this.form.successResponse) }),
        }
        const collectionSettingsResponse = await CollectionsService.updateCollection(this.collectionId, payload)

        if (collectionSettingsResponse.data?.error) {
          this.$notification.error({
            message: 'Something went wrong',
            description: collectionSettingsResponse.data.error.message,
          })
          return
        }

        if (collectionSettingsResponse.data.data) {
          this.$notification.success({
            message: 'Collection settings were successfully updated',
          })
          this.redirectToCollection(collectionSettingsResponse.data.data.name)
        }
      } catch (err) {
        this.$notification.error({
          message: 'Something went wrong',
          description: err.response.data.error.message,
        })
        console.log(err)
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style lang="scss">

</style>
